// Fallback upload constants for the client.
//
// The live app reads the real values from `capabilities.upload_limits`
// (backend `/auth/me`, sourced directly from services/artifacts.py) —
// see CapabilitiesProvider.tsx and SendComposer.tsx. These constants exist
// only as: (a) the pure-function default for `validateFiles` callers that
// don't pass explicit limits, and (b) a fallback for the brief window
// before capabilities load.
//
// Two prod incidents already came from hand-mirroring this list drifting
// from the backend's: `.xlsx` (frontend missing it, valid uploads 422'd
// after the run started) and `.doc` (frontend had it, backend doesn't —
// legacy binary Word has no pure-Python parser). Do not treat this file as
// authoritative; it is not wired to actually gate anything at runtime
// unless capabilities fail to load.

import { buildRequestTarget } from '@/shared/api';

/**
 * Fallback allowed upload file extensions (with leading dot, lowercase).
 * The backend (services/artifacts.py:ALLOWED_UPLOAD_EXTENSIONS) is the
 * real source of truth, served at runtime via capabilities.upload_limits.
 */
export const ALLOWED_UPLOAD_EXTENSIONS: readonly string[] = [
  '.csv',
  '.docx',
  '.html',
  '.jpeg',
  '.jpg',
  '.json',
  '.md',
  '.pdf',
  '.png',
  '.pptx',
  '.tsv',
  '.txt',
  '.css',
  '.yaml',
  '.yml',
  '.xml',
  '.toml',
  '.odt',
  '.rtf',
  '.gif',
  '.webp',
  '.zip',
  '.tar',
  '.gz',
  '.xls',
  '.xlsx',
];

/** Max number of files per send. Mirrors backend MAX_UPLOAD_COUNT. */
export const MAX_UPLOAD_COUNT = 5;

/** Max bytes for any upload or downloadable artifact. Mirrors the backend (25 MB). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * Value for an `<input type="file" accept=...>` attribute, derived from the
 * allowlist so it can never drift from validation.
 */
export const UPLOAD_ACCEPT_ATTR = ALLOWED_UPLOAD_EXTENSIONS.join(',');

/** Extract the lowercase extension (with leading dot) from a filename. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return '';
  return filename.slice(dot).toLowerCase();
}

/** True when the filename's extension is in the allowlist. */
export function isAllowedUploadExtension(filename: string): boolean {
  return ALLOWED_UPLOAD_EXTENSIONS.includes(extensionOf(filename));
}

/**
 * The single place that builds an artifact's `GET /artifacts/{id}/download`
 * URL — used both to fetch a file (FileAttachmentCard) and to reference an
 * already-uploaded file in an outgoing chat message (attachmentAdapter,
 * where the backend resolves the id straight back out of this same URL
 * shape — see the native intake artifact resolver).
 */
export function artifactDownloadUrl(base: string, artifactId: string): string {
  return buildRequestTarget(base, `/api/v1/artifacts/${encodeURIComponent(artifactId)}/download`);
}
